import React, { useState, useRef, useEffect } from 'react';
import { History, Download, Search, MapPin, X } from 'lucide-react';
import C from '../../constants/colors';
import { TX_ACTION_META } from '../../constants/meta';

// ─── MovementPathModal ───────────────────────────────────────────────────────

const PATH_PALETTE = ['#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ef4444', '#14b8a6', '#f97316', '#ec4899'];

function MovementPathModal({ transactions, onClose }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const points = [...transactions]
    .filter(tx => tx.gpsLat != null && tx.gpsLng != null)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const personNames = [...new Set(points.map(p => p.employeeName))];
  const personColor = {};
  personNames.forEach((name, i) => { personColor[name] = PATH_PALETTE[i % PATH_PALETTE.length]; });
  const multiPerson = personNames.length > 1;

  useEffect(() => {
    function initMap() {
      if (mapInstanceRef.current || !mapRef.current || points.length === 0) return;
      const L = window.L;
      const map = L.map(mapRef.current).setView(
        [parseFloat(points[0].gpsLat), parseFloat(points[0].gpsLng)], 15
      );
      L.tileLayer(`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY || ''}`, {
        attribution: '© CartoDB', maxZoom: 19,
      }).addTo(map);
      mapInstanceRef.current = map;

      // Polyline per person
      const byPerson = {};
      points.forEach(p => {
        if (!byPerson[p.employeeName]) byPerson[p.employeeName] = [];
        byPerson[p.employeeName].push(p);
      });
      Object.entries(byPerson).forEach(([name, pts]) => {
        if (pts.length < 2) return;
        L.polyline(
          pts.map(p => [parseFloat(p.gpsLat), parseFloat(p.gpsLng)]),
          { color: personColor[name], weight: 3, opacity: 0.85 }
        ).addTo(map);
      });

      // Numbered markers
      points.forEach((p, i) => {
        const color = personColor[p.employeeName];
        const meta = TX_ACTION_META[p.action] || { label: p.action };
        const icon = L.divIcon({
          html: `<div style="width:26px;height:26px;background:${color};border-radius:50%;border:2px solid white;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:white;box-shadow:0 2px 6px rgba(0,0,0,0.5);line-height:1">${i + 1}</div>`,
          className: '',
          iconSize: [26, 26],
          iconAnchor: [13, 13],
        });
        L.marker([parseFloat(p.gpsLat), parseFloat(p.gpsLng)], { icon })
          .bindPopup(`<b>${p.employeeName}</b><br>${meta.label}<br>${new Date(p.timestamp).toLocaleString()}`)
          .addTo(map);
      });

      if (points.length > 1) {
        map.fitBounds(points.map(p => [parseFloat(p.gpsLat), parseFloat(p.gpsLng)]), { padding: [40, 40] });
      }
    }

    if (window.L) {
      initMap();
    } else if (document.getElementById('leaflet-script')) {
      const iv = setInterval(() => { if (window.L) { clearInterval(iv); initMap(); } }, 100);
      return () => clearInterval(iv);
    } else {
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      const script = document.createElement('script');
      script.id = 'leaflet-script';
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => initMap();
      document.head.appendChild(script);
    }
    return () => {
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '16px', width: '92vw', height: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: C.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MapPin size={16} color={C.blue} />
            Movement Path
            {!multiPerson && points.length > 0 && (
              <span style={{ fontWeight: 400, color: C.textMuted }}>— {points[0].employeeName}</span>
            )}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}>
            <X size={18} />
          </button>
        </div>
        {/* Body */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
          {/* Map */}
          <div ref={mapRef} style={{ flex: 1 }} />
          {/* Sidebar */}
          <div style={{ width: '260px', borderLeft: `1px solid ${C.border}`, overflowY: 'auto', padding: '12px 14px', flexShrink: 0 }}>
            {multiPerson && (
              <>
                <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>People</div>
                {personNames.map(name => (
                  <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.textSecondary, marginBottom: '4px' }}>
                    <span style={{ width: '10px', height: '10px', background: personColor[name], borderRadius: '50%', flexShrink: 0 }} />
                    {name}
                  </div>
                ))}
                <div style={{ borderBottom: `1px solid ${C.border}`, margin: '10px 0' }} />
              </>
            )}
            <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
              {points.length} point{points.length !== 1 ? 's' : ''}
            </div>
            {points.map((p, i) => {
              const meta = TX_ACTION_META[p.action] || { label: p.action, color: C.textMuted };
              const color = personColor[p.employeeName];
              return (
                <div key={p.id} style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ width: '22px', height: '22px', minWidth: '22px', background: color, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '10px', border: '2px solid rgba(255,255,255,0.15)' }}>
                    {i + 1}
                  </div>
                  <div>
                    <div style={{ color: C.textMuted, fontSize: '10px', marginBottom: '2px' }}>{new Date(p.timestamp).toLocaleString()}</div>
                    <span style={{ fontSize: '10px', background: meta.color + '22', color: meta.color, padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>{meta.label}</span>
                    {p.siteName && (
                      <div style={{ color: C.textMuted, fontSize: '10px', marginTop: '2px' }}>
                        {p.siteName}{p.zoneName ? ` · ${p.zoneName}` : ''}
                      </div>
                    )}
                    {multiPerson && <div style={{ color, fontSize: '10px', marginTop: '1px', fontWeight: 500 }}>{p.employeeName}</div>}
                  </div>
                </div>
              );
            })}
            {points.length === 0 && (
              <div style={{ color: C.textMuted, fontSize: '12px' }}>No GPS coordinates in selection.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TransactionLogView ──────────────────────────────────────────────────────

export default function TransactionLogView({ transactions, companies, sites, zones = [] }) {
  const zoneGpsMap = React.useMemo(() => {
    const m = {};
    zones.forEach(z => { if (z.lat != null && z.lng != null) m[z.id] = { lat: z.lat, lng: z.lng }; });
    return m;
  }, [zones]);

  const [filterCompany, setFilterCompany] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showPath, setShowPath] = useState(false);

  const filteredSites = sites.filter(s => !filterCompany || s.companyId === filterCompany);

  const filtered = transactions.filter(tx => {
    if (filterCompany && tx.companyId !== filterCompany) return false;
    if (filterSite && tx.siteId !== filterSite) return false;
    if (filterAction && tx.action !== filterAction) return false;
    if (filterFrom && new Date(tx.timestamp) < new Date(filterFrom)) return false;
    if (filterTo && new Date(tx.timestamp) > new Date(filterTo + 'T23:59:59')) return false;
    if (filterSearch.trim()) {
      const q = filterSearch.toLowerCase();
      if (!tx.employeeName?.toLowerCase().includes(q) && !tx.employeeIdCode?.toLowerCase().includes(q) && !tx.department?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const selectedTxs = transactions.filter(tx => selectedIds.includes(tx.id));
  const selectedWithGps = selectedTxs.filter(tx => tx.gpsLat != null);
  const allFilteredSelected = filtered.length > 0 && filtered.every(tx => selectedIds.includes(tx.id));

  function toggleSelect(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function toggleSelectAll() {
    const filteredIdSet = new Set(filtered.map(tx => tx.id));
    if (allFilteredSelected) {
      setSelectedIds(prev => prev.filter(id => !filteredIdSet.has(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...filtered.map(tx => tx.id)])]);
    }
  }

  function clearSelection() {
    setSelectedIds([]);
  }

  function handleExportCSV() {
    const headers = ['Timestamp','Employee','ID','Department','Role','Action','Source','Company','Site','Zone','Status Before','Status After','Emergency Type','Person GPS Lat','Person GPS Lng','Zone GPS Lat','Zone GPS Lng','Active Event','Event Type'];
    const rows = filtered.map(tx => {
      const storedZLat = tx.zoneGpsLat;
      const storedZLng = tx.zoneGpsLng;
      const liveZone   = tx.zoneId ? zoneGpsMap[tx.zoneId] : null;
      const zLat = storedZLat ?? liveZone?.lat ?? '';
      const zLng = storedZLng ?? liveZone?.lng ?? '';
      return [
        new Date(tx.timestamp).toLocaleString(),
        tx.employeeName, tx.employeeIdCode, tx.department, tx.role,
        tx.action, tx.source, tx.companyName, tx.siteName, tx.zoneName || '',
        tx.statusBefore, tx.statusAfter,
        tx.emergencyType || '',
        tx.gpsLat ?? '', tx.gpsLng ?? '',
        zLat, zLng,
        tx.hasActiveEvent ? 'Yes' : 'No',
        tx.eventType || '',
      ];
    });
    const csv = [headers, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'safecheck-transactions.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const selStyle = { background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 10px', color: C.textPrimary, fontSize: '12px', fontFamily: 'inherit', outline: 'none' };
  const inpStyle = { ...selStyle, width: '140px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <History size={20} color={C.purple} />Transaction Log
          </h2>
          <button onClick={handleExportCSV} style={{ padding: '7px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Download size={13} />Export CSV
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setFilterSite(''); }} style={selStyle}>
            <option value=''>All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterSite} onChange={e => setFilterSite(e.target.value)} style={selStyle}>
            <option value=''>All Sites</option>
            {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={selStyle}>
            <option value=''>All Actions</option>
            {Object.entries(TX_ACTION_META).map(([key, m]) => <option key={key} value={key}>{m.label}</option>)}
          </select>
          <div style={{ position: 'relative' }}>
            <Search size={12} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Name / ID / Dept"
              style={{ ...inpStyle, paddingLeft: '28px', width: '160px' }} />
          </div>
          <input type='date' value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={inpStyle} />
          <input type='date' value={filterTo} onChange={e => setFilterTo(e.target.value)} style={inpStyle} />
          {(filterCompany || filterSite || filterAction || filterSearch || filterFrom || filterTo) && (
            <button onClick={() => { setFilterCompany(''); setFilterSite(''); setFilterAction(''); setFilterSearch(''); setFilterFrom(''); setFilterTo(''); }}
              style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
        <div style={{ background: C.surfaceElevated, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ padding: '10px 12px', width: '36px' }}>
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAll}
                      title={allFilteredSelected ? 'Deselect all visible' : 'Select all visible'}
                      style={{ cursor: 'pointer', accentColor: C.blue }}
                    />
                  </th>
                  {['Date & Time','Employee','ID','Dept','Action','Source','Site','Zone','Status','Event','Person GPS','Zone GPS'].map(h => (
                    <th key={h} style={{ padding: '10px 10px', textAlign: 'left', fontSize: '10px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((tx, i) => {
                  const meta = TX_ACTION_META[tx.action] || { label: tx.action, color: C.textMuted };
                  const isSelected = selectedIds.includes(tx.id);
                  return (
                    <tr key={tx.id} style={{ borderBottom: `1px solid ${C.border}`, background: isSelected ? C.blue + '11' : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)', cursor: 'default' }}>
                      <td style={{ padding: '9px 12px' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(tx.id)}
                          style={{ cursor: 'pointer', accentColor: C.blue }}
                        />
                      </td>
                      <td style={{ padding: '9px 10px', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: C.textSecondary, whiteSpace: 'nowrap' }}>
                        {new Date(tx.timestamp).toLocaleString()}
                      </td>
                      <td style={{ padding: '9px 10px', fontWeight: 600, color: C.textPrimary, whiteSpace: 'nowrap' }}>{tx.employeeName}</td>
                      <td style={{ padding: '9px 10px', fontFamily: 'JetBrains Mono, monospace', color: C.textSecondary, fontSize: '11px' }}>{tx.employeeIdCode}</td>
                      <td style={{ padding: '9px 10px', color: C.textSecondary }}>{tx.department}</td>
                      <td style={{ padding: '9px 10px' }}>
                        <span style={{ fontSize: '11px', background: meta.color + '22', color: meta.color, padding: '2px 7px', borderRadius: '5px', fontWeight: 600, whiteSpace: 'nowrap' }}>{meta.label}</span>
                      </td>
                      <td style={{ padding: '9px 10px', fontSize: '11px', color: C.textMuted }}>{tx.source}</td>
                      <td style={{ padding: '9px 10px', color: C.textSecondary, whiteSpace: 'nowrap' }}>{tx.siteName || '—'}</td>
                      <td style={{ padding: '9px 10px', color: C.textMuted }}>{tx.zoneName || '—'}</td>
                      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '11px', color: C.textMuted }}>{tx.statusBefore}</span>
                        <span style={{ fontSize: '11px', color: C.textMuted, margin: '0 4px' }}>→</span>
                        <span style={{ fontSize: '11px', color: tx.statusAfter === 'SAFE' ? C.green : tx.statusAfter === 'NEED_HELP' ? C.red : tx.statusAfter === 'EVACUATED' ? C.orange : C.textSecondary, fontWeight: 600 }}>{tx.statusAfter}</span>
                      </td>
                      <td style={{ padding: '9px 10px', fontSize: '11px', color: tx.hasActiveEvent ? C.amber : C.textMuted, whiteSpace: 'nowrap' }}>
                        {tx.hasActiveEvent ? (tx.eventType || 'Active') : '—'}
                      </td>
                      <td style={{ padding: '9px 10px', fontFamily: 'JetBrains Mono, monospace', fontSize: '10px', whiteSpace: 'nowrap' }}>
                        {tx.gpsLat != null
                          ? <span style={{ color: C.blue }}>{parseFloat(tx.gpsLat).toFixed(4)}, {parseFloat(tx.gpsLng).toFixed(4)}</span>
                          : <span style={{ color: C.textMuted }}>—</span>}
                      </td>
                      <td style={{ padding: '9px 10px', fontFamily: 'JetBrains Mono, monospace', fontSize: '10px', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const stored = tx.zoneGpsLat != null ? { lat: tx.zoneGpsLat, lng: tx.zoneGpsLng } : null;
                          const live   = tx.zoneId ? zoneGpsMap[tx.zoneId] : null;
                          const gps    = stored || live;
                          return gps
                            ? <span style={{ color: C.green }}>{parseFloat(gps.lat).toFixed(4)}, {parseFloat(gps.lng).toFixed(4)}</span>
                            : <span style={{ color: C.textMuted }}>—</span>;
                        })()}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={13} style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>No transactions match current filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={{ padding: '12px 24px', borderTop: `1px solid ${C.border}`, background: C.surface, fontSize: '12px', color: C.textMuted, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <span>
          Showing <strong style={{ color: C.textPrimary }}>{filtered.length}</strong> of <strong style={{ color: C.textPrimary }}>{transactions.length}</strong> transactions (permanent forensic log — never deleted)
        </span>
        {selectedIds.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <span style={{ color: C.textSecondary }}>
              <strong style={{ color: C.textPrimary }}>{selectedIds.length}</strong> selected
              {selectedWithGps.length < selectedIds.length && (
                <span style={{ color: C.textMuted }}> · {selectedWithGps.length} with GPS</span>
              )}
            </span>
            <button
              onClick={clearSelection}
              style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Clear
            </button>
            <button
              onClick={() => setShowPath(true)}
              disabled={selectedWithGps.length < 2}
              title={selectedWithGps.length < 2 ? 'Select at least 2 transactions with GPS coordinates' : 'Draw movement path on map'}
              style={{
                padding: '6px 14px', borderRadius: '8px', border: 'none',
                background: selectedWithGps.length >= 2 ? C.blue : C.border,
                color: selectedWithGps.length >= 2 ? '#fff' : C.textMuted,
                fontSize: '12px', fontWeight: 600, cursor: selectedWithGps.length >= 2 ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <MapPin size={13} />
              Draw Path ({selectedWithGps.length})
            </button>
          </div>
        )}
      </div>

      {showPath && (
        <MovementPathModal
          transactions={selectedTxs}
          onClose={() => setShowPath(false)}
        />
      )}
    </div>
  );
}
